import React, { useState } from 'react'
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { CreditCardIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import Spinner from '../Spinner'
import { getStripe, stripeKeyMode } from '../../services/stripe'
import { PaymentsNotSetUp, SecurePaymentsLine, TestModeNotice } from './StripeTrust'
import { isTerminalThemeEnabled } from '../../theme/terminalTheme'

interface PaymentMethod {
  id: string
  type: string
  last4: string
  brand?: string
  expiryMonth?: number
  expiryYear?: number
  isDefault: boolean
}

interface PaymentMethodFormProps {
  existingMethods?: PaymentMethod[]
  onAddPaymentMethod: (method: { payment_method_id: string }) => Promise<void>
  onSetDefault?: (methodId: string) => Promise<void>
  onRemove?: (methodId: string) => Promise<void>
}

interface CardAddFormProps {
  onSubmit: (method: { payment_method_id: string }) => Promise<void>
  onCancel: () => void
  /** Off where the page already shows "Secure payments by Stripe". */
  showSecureLine?: boolean
}

/** Stripe's card field and the submit button; render inside <Elements>. */
export const CardAddForm: React.FC<CardAddFormProps> = ({ onSubmit, onCancel, showSecureLine = true }) => {
  const stripe = useStripe()
  const elements = useElements()
  const [name, setName] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!stripe || !elements) {
      setError('Payment processing is unavailable. Please reload the page.')
      return
    }

    const cardElement = elements.getElement(CardElement)
    if (!cardElement) {
      setError('Card information is incomplete.')
      return
    }

    setIsLoading(true)
    try {
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: name ? { name } : undefined,
      })

      if (stripeError) {
        setError(stripeError.message || 'Failed to process card')
        return
      }

      await onSubmit({ payment_method_id: paymentMethod!.id })
    } catch (err: any) {
      setError(err.message || 'Failed to add payment method')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
          {error}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Cardholder Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="John Doe"
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Card Details</label>
        <div className="px-4 py-3 border border-gray-300 rounded-md">
          {/* Stripe renders the card field in its own iframe, out of reach of
              the page stylesheet, so its text colour must follow the theme here. */}
          <CardElement
            options={{
              style: {
                base: isTerminalThemeEnabled()
                  ? { fontSize: '16px', color: '#f3f4f6', '::placeholder': { color: '#8b98a5' } }
                  : { fontSize: '16px', color: '#374151' },
              },
            }}
          />
        </div>
        {/* Backend services/buyer_country.py: until FinCreator is registered
            for VAT abroad it takes payment only from US-issued cards. */}
        <p className="mt-1 text-xs text-gray-500" data-testid="us-cards-only">
          For now FinCreator takes cards issued in the United States only.
        </p>
      </div>

      <TestModeNotice />

      <div className="flex gap-3 pt-4">
        <button
          type="button"
          onClick={() => {
            setError(null)
            onCancel()
          }}
          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isLoading && <Spinner size="sm" className="border-white border-t-transparent" />}
          {isLoading ? 'Adding' : 'Add Payment Method'}
        </button>
      </div>

      {showSecureLine && <SecurePaymentsLine />}
    </form>
  )
}

const PaymentMethodForm: React.FC<PaymentMethodFormProps> = ({
  existingMethods = [],
  onAddPaymentMethod,
  onSetDefault,
  onRemove,
}) => {
  const [showAddForm, setShowAddForm] = useState(false)
  // No publishable key: Stripe can't load, so say so instead of an empty card box.
  const paymentsReady = stripeKeyMode() !== 'missing'

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-6">Payment Methods</h2>

      {existingMethods.length > 0 && (
        <div className="space-y-3 mb-6">
          {existingMethods.map((method) => (
            <div
              key={method.id}
              className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <div className="flex items-center gap-3">
                <CreditCardIcon className="h-6 w-6 text-gray-600" />
                <div>
                  <p className="font-medium text-gray-900">
                    {method.brand || 'Card'} ****{method.last4}
                  </p>
                  {method.expiryMonth && method.expiryYear && (
                    <p className="text-sm text-gray-600">
                      Expires {method.expiryMonth}/{method.expiryYear}
                    </p>
                  )}
                  {method.isDefault && (
                    <span className="inline-flex items-center text-xs text-green-600 mt-1">
                      <CheckCircleIcon className="h-3 w-3 mr-1" />
                      Default
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                {!method.isDefault && onSetDefault && (
                  <button
                    onClick={() => onSetDefault(method.id)}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Set as default
                  </button>
                )}
                {onRemove && (
                  <button
                    onClick={() => onRemove(method.id)}
                    className="text-sm text-red-600 hover:text-red-700 font-medium"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!paymentsReady ? (
        <PaymentsNotSetUp />
      ) : !showAddForm ? (
        <button
          onClick={() => setShowAddForm(true)}
          className="w-full px-4 py-2 border-2 border-dashed border-gray-300 text-gray-600 rounded-lg hover:border-blue-500 hover:text-blue-600 transition-colors font-medium"
        >
          + Add Payment Method
        </button>
      ) : (
        <div className="border border-gray-200 rounded-lg p-4">
          <Elements stripe={getStripe()}>
            <CardAddForm
              onSubmit={async (method) => {
                await onAddPaymentMethod(method)
                setShowAddForm(false)
              }}
              onCancel={() => setShowAddForm(false)}
            />
          </Elements>
        </div>
      )}
    </div>
  )
}

export default PaymentMethodForm
